/**
 * HRManagement constants — single source of truth for status IDs, role names,
 * and notification type IDs that the UI maps to labels and badge colours.
 */

// Backend role string literals — kept inline since they're stable,
// well-known IdentityServer role names. Mirrors the backend's seeded
// roles in `dbo.AspNetRoles`.
export const HR_ROLES = {
  ADMIN: 'HRAdmin',
  STAFF: 'Staff',
} as const;

/**
 * Leave status IDs — must match the seeded rows in `dbo.LeaveStatus`:
 *
 *   ID  | Name      | Description
 *   ----|-----------|-------------------------------------------------------
 *   -2  | Cancelled | Initially approved leave that has been cancelled
 *   -1  | Rejected  | Reviewed and denied
 *    0  | Pending   | Submitted, awaiting approval
 *    1  | Approved  | Reviewed and approved
 *    2  | Deferred  | Postponed to a later date
 *
 * Wherever possible, the UI should render the `statusName` / `statusColor`
 * fields from `LeaveModelView` (joined from this same DB table) instead of
 * relying on these labels. The constants here are a fallback when only the
 * raw `Leave` entity is available (so the row at least gets a label rather
 * than `#0`).
 */
export const LEAVE_STATUS = {
  CANCELLED: -2,
  REJECTED: -1,
  PENDING: 0,
  APPROVED: 1,
  DEFERRED: 2,
} as const;

export type LeaveStatusId = (typeof LEAVE_STATUS)[keyof typeof LEAVE_STATUS];

export const LEAVE_STATUS_LABELS: Record<number, string> = {
  [LEAVE_STATUS.CANCELLED]: 'Cancelled',
  [LEAVE_STATUS.REJECTED]: 'Rejected',
  [LEAVE_STATUS.PENDING]: 'Pending',
  [LEAVE_STATUS.APPROVED]: 'Approved',
  [LEAVE_STATUS.DEFERRED]: 'Deferred',
};

export const LEAVE_STATUS_COLORS: Record<number, string> = {
  [LEAVE_STATUS.CANCELLED]: 'gray',
  [LEAVE_STATUS.REJECTED]: 'red',
  [LEAVE_STATUS.PENDING]: 'yellow',
  [LEAVE_STATUS.APPROVED]: 'green',
  [LEAVE_STATUS.DEFERRED]: 'blue',
};

/** Policy assignment status strings — match backend `PolicyAssignment.Status`. */
export const POLICY_ASSIGNMENT_STATUS = {
  PENDING: 'Pending',
  SIGNED: 'Signed',
  OVERDUE: 'Overdue',
  WAIVED: 'Waived',
  SUPERSEDED: 'Superseded',
} as const;

export const POLICY_ASSIGNMENT_STATUS_COLORS: Record<string, string> = {
  Pending: 'yellow',
  Signed: 'green',
  Overdue: 'red',
  Waived: 'gray',
  Superseded: 'gray',
};

/** HR notification type IDs from `HRNotificationChannelSetting`. */
export const HR_NOTIFICATION_TYPES = {
  LeaveAppliedAdmin: 1,
  LeaveApproved: 2,
  LeaveRejected: 3,
  LeaveCancelled: 4,
  LeaveFileAdded: 5,
  // Leave approval chain (HR migration 003)
  LeaveAwaitingApproval: 6,
  LeaveApprovalProgress: 7,
  LeaveApprovalReminder: 8,
  LeaveApprovalAskEmployee: 9,
  LeaveStartingSoonReminder: 10,
  LeaveStartedConfirmation: 11,
  LeaveEndingSoonReminder: 12,
  LeaveCompletedConfirmation: 13,
  LeavePendingEscalation: 14,
  LeaveOverlapAlert: 15,
  LeaveApprovalAskAnswered: 16,
  LeaveDutyCoverAssigned: 17,
  EmployeeOnboarded: 20,
  EmployeeStatusChanged: 21,
  EmployeeAnniversary: 22,
  EmployeeBirthdayReminder: 23,
  EmployeeDocumentRequired: 24,
  EmployeeFileAdded: 25,
  EmployeeFileExpiring: 26,
  EmployeeDutyAssigned: 27,
  EmployeeDutyRemoved: 28,
  PolicyPublished: 30,
  PolicyUpdated: 31,
  PolicySignatureRequired: 32,
  PolicySignatureReminder: 33,
  PolicySignatureOverdueEscalation: 34,
  PolicySigned: 35,
  PolicyArchived: 36,
  HRDailyDigestAdmin: 40,
  HRWeeklyDigestAdmin: 41,
  HRMonthlyDigestAdmin: 42,
} as const;

/**
 * Friendly names for notification types whose seeded `notificationTypeName` is a
 * code name. HR Settings › Channels prefers these over the raw name, so the leave
 * approval types read well next to the older rows.
 */
export const HR_NOTIFICATION_TYPE_LABELS: Record<number, string> = {
  [HR_NOTIFICATION_TYPES.LeaveAwaitingApproval]: 'Leave waiting for your approval',
  [HR_NOTIFICATION_TYPES.LeaveApprovalProgress]: 'Leave approved by line approver',
  [HR_NOTIFICATION_TYPES.LeaveApprovalReminder]: 'Reminder: leave waiting for your approval',
  [HR_NOTIFICATION_TYPES.LeaveApprovalAskEmployee]: 'Approver needs something from the employee',
  [HR_NOTIFICATION_TYPES.LeaveApprovalAskAnswered]: 'Employee answered the approver',
  [HR_NOTIFICATION_TYPES.LeaveDutyCoverAssigned]: 'Asked to cover a colleague\'s duties',
};

/** File-management `domain` strings used by the FileUploader for HR. */
export const HR_FILE_DOMAINS = {
  EMPLOYEE_FILE: 'EmployeeFile',
  LEAVE_DOCUMENT: 'LeaveDocument',
  POLICY: 'Policy',
  POLICY_SIGNATURE: 'PolicySignature',
} as const;
